import { defineStore } from "pinia";
import { useFormStore } from "@/stores/form";
import { calculateAutarky, calculateSelfConsumption } from "~/lib/autonomy";
import * as constants from "~/lib/constants";


export const useSolarPotentialStore = defineStore("solarPotential", {
  persist: true,
  state: () => {
    return {
      // Solar panel configurations
      solarPanelConfigs: {},
      // Selected solar panel count
      selectedPanelCount: 8,
      // Consumption in kWh
      consumption: 4000,
      // Battery capacity in kWh
      batteryCapacity: 0,
      // Heating consumption in kWh
      heatingConsumption: 0,
      // Cooling consumption in kWh
      coolingConsumption: 0,
      // Electric car consumption in kWh
      electricCarConsumption: 0,
    }
  },
  getters: {
    // Get energy production in kWh from the selected solar panel configuration
    energyProduction: (state) => {
      if (state.selectedPanelCount && state.solarPanelConfigs[state.selectedPanelCount]) {
        return Math.round(state.solarPanelConfigs[state.selectedPanelCount].realYearlyEnergyDcKwh);
      }

      return 0;
    },
    // Get the total consumption in kWh including heating, cooling, and electric car consumption
    totalConsumption: (state) => {
      return state.consumption + state.heatingConsumption + state.coolingConsumption + state.electricCarConsumption;
    },
    // Get the minimum number of solar panels, that can be installed
    minPanels: (state) => {
      if (state.solarPanelConfigs) {
        return Object.values(state.solarPanelConfigs).find( c => typeof c !== undefined)?.realPanelsCount;
      }

      return 8;
    },
    // Get the maximum number of solar panels, that can be installed
    maxPanels: (state) => {
      if (state.solarPanelConfigs) {
        return Object.values(state.solarPanelConfigs).at(-1)?.realPanelsCount;
      }

      return 50;
    },
    // Get the CO2 savings in tons from the selected solar panel configuration
    co2Savings() {
      return this.energyProduction * constants.CO2_EMISSIONS_PER_KWH / 1000;
      // return state.solarPanelConfigs[state.selectedPanelCount].co2Savings;
    },
    // Get number of flights (DTM - PMI) equivalent to the CO2 savings
    flights() {
      return this.co2Savings / constants.CO2_EMISSIONS_PER_FLIGHT;
    },
    // Get number of trees needed to offset the CO2 savings
    trees() {
      return this.co2Savings / constants.CO2_UPTAKE_PER_TREE;
    },
    // Calculate the autarky percentage based on the current energy production, consumption, and battery capacity
    autarky() {
      // const autarky = calculateAutarky(this.selectedPanelCount * 430 / 1000, this.totalConsumption, this.batteryCapacity);
      const autarky = calculateAutarky(this.energyProduction / 1000, this.totalConsumption, this.batteryCapacity);

      if (isNaN(autarky)) {
        return 0;
      }

      return autarky;
    },
    // Calculate the self consumption percentage based on the current energy production, consumption, and battery capacity
    selfConsumption() {
      // const selfConsumption = calculateSelfConsumption(this.selectedPanelCount * 430 / 1000, this.totalConsumption, this.batteryCapacity);
      const selfConsumption = calculateSelfConsumption(this.energyProduction / 1000, this.totalConsumption, this.batteryCapacity);

      if (isNaN(selfConsumption)) {
        return 0;
      }

      return selfConsumption;
    },
    // Calculate the cost savings in Euro based on the current energy production and the energy price
    costSavings() {
      return ((this.autarky * this.totalConsumption / 100) * constants.ENERGY_PRICE);
    },
    // Calculate how much of the energy production is sold
    soldEnergy() {
      return (((100 - this.selfConsumption) * this.energyProduction / 100) * constants.ENERGY_PRICE_SELLING);
    },
    totalCostSavings() {
      return (this.costSavings + this.soldEnergy);
    },
    // Maximum cost savings in Euro with heat pump in conjunction with solar panels
    costSavingsHeatPump() {
      return (this.heatingConsumption * 0.5 * constants.ENERGY_PRICE); // Assuming 50% of the heating consumption is covered by the pv system
    },
    // Maximum gas consumption in kWh based on the building type
    gasConsumption() {
      const formStore = useFormStore();
      let gasConsumption = 0;

      gasConsumption = Math.round(formStore.square_meters * constants.GAS_AVARAGE_CONSUMPTION);

      return gasConsumption;
    },
    // Maximum CO2 savings in tons with heat pump in conjunction with solar panels compared to gas heating
    co2SavingsHeatPump() {
      const gasCo2Emissions = this.gasConsumption * constants.GAS_CO2_EMISSIONS_PER_KWH;
      const heatPumpCo2Emissions = (this.heatingConsumption * 0.5) * constants.CO2_EMISSIONS_PER_KWH; // Assuming 50% of the heating consumption is covered by the pv system
      const co2Savings = ((gasCo2Emissions - heatPumpCo2Emissions) / 1000);

      return co2Savings > 0 ? co2Savings : 0;
    },
    // Maximum cost savings in Euro with heat pump in conjunction with solar panels compared to gas heating
    heatingPriceSavings() {
      const gasConsumptionPrice = this.gasConsumption * constants.GAS_PRICE;
      const heatPumpConsumptionPrice = (this.heatingConsumption * 0.5) * constants.ENERGY_PRICE; // Assuming 50% of the heating consumption is covered by the pv system
      const priceSavings = (gasConsumptionPrice - heatPumpConsumptionPrice);

      return priceSavings > 0 ? priceSavings : 0;
    },
    // Get energy consumption of electric car supplied by the solar panels
    electricCarConsumptionFromPanels() {
      return this.electricCarConsumption * this.autarky / 100 ?? 0;
    },
    // Get co2 savings of electric car
    co2SavingsElectricCar() {
      const co2Savings = this.electricCarConsumptionFromPanels * constants.CO2_EMISSIONS_PER_KWH / 1000 ?? 0;

      return co2Savings;
    },
    // Get km of electric car supplied by the solar panels
    electricCarKm() {
      return this.electricCarConsumptionFromPanels / constants.E_CAR_CONSUMPTION ?? 0;
    }
  },
  actions: {
    // Set the selected solar panel count
    setSelectedPanelCount(count) {
      for (let i = count; i <= this.maxPanels; i++) {
        if (this.solarPanelConfigs[i]) {
          this.selectedPanelCount = i;
          break;
        }
      }
    },
    // Set the solar panel configurations
    setSolarPanelConfigs(configs) {
      this.solarPanelConfigs = structuredClone(configs);
    },
    // Set the consumption in kWh for reports page, removes heating, cooling, and electric car consumption
    setConsumption(consumption) {
      this.consumption = consumption - this.heatingConsumption - this.coolingConsumption - this.electricCarConsumption;
    },
    setBatteryCapacity(capacity) {
      this.batteryCapacity = capacity;
    },
    // Set the default battery capacity based on the consumption in kWh
    setDefaultBatteryCapacity() {
      const capacity = ((this.totalConsumption / 1000) * 2).toFixed();
      this.batteryCapacity = capacity > 20 ? 20 : capacity;
    },
    // Set optimal panel based on autarky equal 70% or more
    setOptimalPanelCount() {
      let optimalPanelCount = 0;
      for (const [index, config] of Object.entries(this.solarPanelConfigs)) {
        optimalPanelCount = index;
        const energyKwp = config.realYearlyEnergyDcKwh / 1000;

        if (calculateAutarky(energyKwp, this.totalConsumption, this.batteryCapacity) >= 70) {
          break;
        }
      }

      this.selectedPanelCount = optimalPanelCount;
    },
    // Update the consumption based on the selected planned consumers in the form.
    // Resets also the battery capacity and sets the optimal panel count.
    updateConsumptionOnConsumersChange() {
      const formStore = useFormStore();
      this.heatingConsumption = 0;
      this.coolingConsumption = 0;
      this.electricCarConsumption = 0;

      // Check if planned consumers includes heat pump and calculate the heating consumption, if so
      if (formStore.planned_consumers.includes('Wärmepumpe')) {
        this.heatingConsumption = Math.round((formStore.square_meters * formStore.construction_year) / constants.JAZ * constants.HEATING_HOURS);
      }

      // Check if planned consumers includes electric heating and calculate and add the heating consumption, if so.
      if (formStore.planned_consumers.includes('E-Heizung')) {
        this.heatingConsumption += Math.round((formStore.square_meters * formStore.construction_year) * constants.HEATING_HOURS);
      }

      // Check if planned consumers includes air conditioner and calculate the cooling consumption, if so
      if (formStore.planned_consumers.includes('Klimaanlage')) {
        this.coolingConsumption = Math.round(formStore.square_meters * constants.COOLING_PERFORMANCE_LOW * constants.COOLING_HOURS);
      }

      // Check if planned consumers includes electric car and calculate the electric car consumption, if so
      if (formStore.planned_consumers.includes('E-Auto')) {
        this.electricCarConsumption = Math.round(constants.E_CAR_CONSUMPTION * constants.E_CAR_KM_PER_YEAR * 1);
      }

      // Then set default battery capacity based on if battery is planed
      if (formStore.battery_storage_planned) {
        this.setDefaultBatteryCapacity();
      } else {
        this.batteryCapacity = 0;
      }

      // Lastly set the optimal panel count based on the new consumption and battery capacity
      this.setOptimalPanelCount();
    },
    resetSolarPotential() {
      this.solarPanelConfigs = {};
      this.selectedPanelCount = 8;
      this.consumption = 2500;
      this.batteryCapacity = 0;
      this.heatingConsumption = 0;
      this.coolingConsumption = 0;
      this.electricCarConsumption = 0;
    }
  }
});
